import React from 'react';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';

// Monthly expenditure data
const monthlyData = [
  { name: 'Jan', amount: 45000 },
  { name: 'Feb', amount: 52000 },
  { name: 'Mar', amount: 48000 },
  { name: 'Apr', amount: 61000 },
  { name: 'May', amount: 55000 },
  { name: 'Jun', amount: 67000 },
  { name: 'Jul', amount: 72000 },
  { name: 'Aug', amount: 59000 },
  { name: 'Sep', amount: 63000 },
  { name: 'Oct', amount: 47000 },
  { name: 'Nov', amount: 51000 },
  { name: 'Dec', amount: 64000 },
];

// Updated regional expenditure data with new regions
const regionalData = [
  { name: 'MENA', value: 280000 },
  { name: 'Europe', value: 320000 },
  { name: 'North America', value: 410000 },
  { name: 'Asia Pacific', value: 190000 },
];

// Updated case type to practice group data
const practiceGroupData = [
  { name: 'Corporate', value: 520000 },
  { name: 'Litigation', value: 380000 },
  { name: 'IP', value: 240000 },
  { name: 'Regulatory', value: 160000 },
];

// YoY comparison data
const yoyData = [
  { name: 'Jan', thisYear: 45000, lastYear: 39000 },
  { name: 'Feb', thisYear: 52000, lastYear: 41000 },
  { name: 'Mar', thisYear: 48000, lastYear: 43000 },
  { name: 'Apr', thisYear: 61000, lastYear: 55000 },
  { name: 'May', thisYear: 55000, lastYear: 49000 },
  { name: 'Jun', thisYear: 67000, lastYear: 58000 },
  { name: 'Jul', thisYear: 72000, lastYear: 62000 },
  { name: 'Aug', thisYear: 59000, lastYear: 51000 },
  { name: 'Sep', thisYear: 63000, lastYear: 55000 },
  { name: 'Oct', thisYear: 47000, lastYear: 41000 },
  { name: 'Nov', thisYear: 51000, lastYear: 46000 },
  { name: 'Dec', thisYear: 64000, lastYear: 53000 },
];

// Color palette
const COLORS = ['hsl(217, 91%, 60%)', 'hsl(25, 95%, 53%)', 'hsl(262, 83%, 58%)', 'hsl(48, 96%, 53%)'];
const BAR_COLOR = 'hsl(217, 91%, 60%)';
const LINE_COLORS = {
  thisYear: 'hsl(217, 91%, 60%)',
  lastYear: 'hsl(25, 95%, 53%)'
};

const chartConfig = {
  current: {
    theme: {
      light: 'hsl(217, 91%, 60%)',
      dark: 'hsl(217, 91%, 60%)',
    },
  },
  previous: {
    theme: {
      light: 'hsl(25, 95%, 53%)',
      dark: 'hsl(25, 95%, 53%)',
    },
  },
};

interface LegalAnalyticsProps {
  variant?: 'all' | 'monthly' | 'regional' | 'yoy' | 'practice';
}

const LegalAnalytics = ({ variant = 'all' }: LegalAnalyticsProps) => {
  // Monthly Expenditure Chart
  const MonthlyChart = () => (
    <ChartContainer config={chartConfig} className="h-full w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={monthlyData}
          margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
          <YAxis 
            tickFormatter={(value) => `$${value/1000}k`}
            tick={{ fontSize: 11 }}
            stroke="hsl(var(--muted-foreground))"
          />
          <ChartTooltip
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                return (
                  <ChartTooltipContent
                    formatter={(value) => [`$${Number(value).toLocaleString()}`, 'Expenditure']}
                  />
                );
              }
              return null;
            }}
          />
          <Bar dataKey="amount" fill={BAR_COLOR} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartContainer>
  );

  // Regional Expenditure Chart - No gaps between segments
  const RegionalChart = () => (
    <ChartContainer config={chartConfig} className="h-full w-full">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={regionalData}
            cx="50%"
            cy="50%"
            labelLine={false}
            outerRadius={70}
            innerRadius={0}
            fill="#8884d8"
            dataKey="value"
            stroke="none"
            strokeWidth={0}
            paddingAngle={0}
            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
            style={{ fontSize: '12px', fontWeight: 500 }}
          >
            {regionalData.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={COLORS[index % COLORS.length]} 
                stroke="none"
                strokeWidth={0}
              />
            ))}
          </Pie>
          <ChartTooltip
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                return (
                  <ChartTooltipContent
                    formatter={(value) => [`$${Number(value).toLocaleString()}`, 'Expenditure']}
                  />
                );
              }
              return null;
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </ChartContainer>
  );

  // Year-over-Year Chart
  const YoYChart = () => (
    <ChartContainer config={chartConfig} className="h-full w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={yoyData}
          margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
          <YAxis 
            tickFormatter={(value) => `$${value/1000}k`}
            tick={{ fontSize: 11 }}
            stroke="hsl(var(--muted-foreground))"
          />
          <ChartTooltip content={<ChartTooltipContent />} />
          <Line
            type="monotone"
            dataKey="thisYear"
            stroke={LINE_COLORS.thisYear}
            name="This Year"
            strokeWidth={2}
            dot={false}
          />
          <Line
            type="monotone"
            dataKey="lastYear"
            stroke={LINE_COLORS.lastYear}
            name="Last Year"
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartContainer>
  );

  // Practice Group Chart
  const PracticeChart = () => (
    <ChartContainer config={chartConfig} className="h-full w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={practiceGroupData}
          layout="vertical"
          margin={{ top: 5, right: 10, left: 60, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
          <XAxis 
            type="number" 
            tickFormatter={(value) => `$${value/1000}k`}
            tick={{ fontSize: 11 }}
            stroke="hsl(var(--muted-foreground))"
          />
          <YAxis 
            type="category" 
            dataKey="name" 
            tick={{ fontSize: 12, fontWeight: 500 }}
            width={55}
            stroke="hsl(var(--muted-foreground))"
          />
          <ChartTooltip
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                return (
                  <ChartTooltipContent
                    formatter={(value) => [`$${Number(value).toLocaleString()}`, 'Expenditure']}
                  />
                );
              }
              return null;
            }}
          />
          <Bar dataKey="value" name="Value" radius={[0, 4, 4, 0]}>
            {practiceGroupData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartContainer>
  );

  // Render based on variant
  if (variant === 'monthly') return <MonthlyChart />;
  if (variant === 'regional') return <RegionalChart />;
  if (variant === 'yoy') return <YoYChart />;
  if (variant === 'practice') return <PracticeChart />;

  // Default: render all in a grid (legacy support)
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="h-[180px]"><MonthlyChart /></div>
      <div className="h-[180px]"><RegionalChart /></div>
      <div className="h-[180px]"><YoYChart /></div>
      <div className="h-[180px]"><PracticeChart /></div>
    </div>
  );
};

export default LegalAnalytics;